import { useEffect, useState } from 'react';
import type { UserProfile } from '@/state/userStore';
import { hasPremiumAccess } from '@/services/entitlements';
import { loadLastKnownEntitlementUi } from '@/services/entitlementUiCache';
import { getCachedPremiumFromRevenueCat } from '@/services/revenuecat';
import { networkMonitor, useNetworkStatus } from '@/services/networkMonitor';

export type OfflinePlanHintSource = 'none' | 'firestore_cache' | 'revenuecat';

export type OfflinePlanTierDisplay = {
  effectivePremium: boolean;
  showOfflineHint: boolean;
  hintSource: OfflinePlanHintSource;
};

const sameOfflinePlanDisplay = (
  a: OfflinePlanTierDisplay,
  b: OfflinePlanTierDisplay,
): boolean =>
  a.effectivePremium === b.effectivePremium &&
  a.showOfflineHint === b.showOfflineHint &&
  a.hintSource === b.hintSource;

/**
 * Profile-only plan tier: when offline and the store profile looks free, fall back to
 * last-known Firestore snapshot or RevenueCat CustomerInfo cache.
 */
export function useOfflinePlanTierDisplay(
  uid: string | undefined,
  profile: Pick<UserProfile, 'isPremium' | 'planTier' | 'entitlement'>,
): OfflinePlanTierDisplay {
  const networkStatus = useNetworkStatus();
  const [display, setDisplay] = useState<OfflinePlanTierDisplay>(() => {
    const storePremium = hasPremiumAccess(profile);
    return {
      effectivePremium: storePremium,
      showOfflineHint: false,
      hintSource: 'none',
    };
  });

  useEffect(() => {
    const storePremium = hasPremiumAccess(profile);
    const offline = networkMonitor.shouldUseOfflineDataFallback();

    if (!uid) {
      setDisplay((prev) => {
        const next = { effectivePremium: false, showOfflineHint: false, hintSource: 'none' as const };
        return sameOfflinePlanDisplay(prev, next) ? prev : next;
      });
      return;
    }

    if (!offline || storePremium) {
      setDisplay((prev) => {
        const next = {
          effectivePremium: storePremium,
          showOfflineHint: false,
          hintSource: 'none' as const,
        };
        return sameOfflinePlanDisplay(prev, next) ? prev : next;
      });
      return;
    }

    let cancelled = false;

    const run = async () => {
      const cached = await loadLastKnownEntitlementUi(uid);
      const cachedPremium =
        cached != null &&
        hasPremiumAccess({
          isPremium: cached.isPremium,
          planTier: cached.planTier,
          entitlement: cached.entitlement,
        });
      if (cancelled) return;
      if (cachedPremium) {
        setDisplay((prev) => {
          const next = {
            effectivePremium: true,
            showOfflineHint: true,
            hintSource: 'firestore_cache' as const,
          };
          return sameOfflinePlanDisplay(prev, next) ? prev : next;
        });
        return;
      }
      const rc = await getCachedPremiumFromRevenueCat();
      if (cancelled) return;
      if (rc) {
        setDisplay((prev) => {
          const next = {
            effectivePremium: true,
            showOfflineHint: true,
            hintSource: 'revenuecat' as const,
          };
          return sameOfflinePlanDisplay(prev, next) ? prev : next;
        });
        return;
      }
      setDisplay((prev) => {
        const next = {
          effectivePremium: false,
          showOfflineHint: false,
          hintSource: 'none' as const,
        };
        return sameOfflinePlanDisplay(prev, next) ? prev : next;
      });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    uid,
    profile.isPremium,
    profile.planTier,
    profile.entitlement?.tier,
    profile.entitlement?.status,
    networkStatus.isConnected,
    networkStatus.isInternetReachable,
  ]);

  return display;
}
